import {FieldNames} from '@hconnect/tuc-processes/fields'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {Box} from '@mui/material'
import React, {useMemo} from 'react'

import type {FieldConfiguration} from '../../../types/fieldConfiguration'
import {FieldNameMapping, FIELD_ID_TO_FIELD_NAMES} from '../../../utils/fieldIdToFieldNames'
import {
  getFieldButtonStateProps,
  getCombinedFieldButtonStateProps
} from '../../../utils/getFieldButtonStateProps'

/**
 * Hook to get combined state props for one or more field names.
 * Handles both single fields and composite fields (arrays).
 *
 * Uses the enhanced useChangeableField hook that supports arrays,
 * then combines the states for composite fields.
 */
const useFieldButtonState = (fieldNameMapping: FieldNameMapping) => {
  // Normalize to array for consistent handling
  const fieldNames = useMemo(() => {
    if (Array.isArray(fieldNameMapping)) {
      return fieldNameMapping as FieldNames[]
    }
    return [fieldNameMapping as FieldNames]
  }, [fieldNameMapping])

  // Get states for all fields at once
  const fieldStates = useChangeableField(fieldNames)

  // Skip merge for single result
  if (fieldStates.length === 1) {
    return getFieldButtonStateProps(fieldStates[0])
  }

  // For composite fields, combine the states
  return getCombinedFieldButtonStateProps(fieldStates)
}

/**
 * Props for the FieldButtonWithState wrapper component
 */
export interface FieldButtonWithStateProps {
  /** Field configuration containing the fieldId and FieldButtonComponent */
  config: FieldConfiguration
  /** Callback when the field button is clicked */
  onFieldClick: (fieldId: string) => void
}

/**
 * Wrapper component that provides changeable field state to a FieldButtonComponent.
 * Uses the useChangeableField hook to get the current state for the field.
 *
 * This component bridges the gap between the field configuration system and
 * the changeable fields system from tuc-processes, automatically determining:
 * - Whether the field is read-only (cannot be changed)
 * - Whether the field value has been changed
 * - Whether changes require dispatcher approval
 *
 * For composite fields (like intervals which maps to spacingOrFlowRate AND timeOnJobSite),
 * the states are combined:
 * - isReadOnly: true only if ALL fields are not changeable
 * - isChanged: true if ANY field is dirty
 * - requiresApproval: true if ANY dirty field requires dispatch confirmation
 *
 * @example
 * <FieldButtonWithState
 *   config={materialFieldConfig}
 *   onFieldClick={(fieldId) => navigateToEditor(fieldId)}
 * />
 */
export const FieldButtonWithState: React.FC<FieldButtonWithStateProps> = ({
  config,
  onFieldClick
}) => {
  const {fieldId, FieldButtonComponent} = config
  const fieldNameMapping = FIELD_ID_TO_FIELD_NAMES[fieldId]
  const stateProps = useFieldButtonState(fieldNameMapping)

  return (
    <Box data-test-id={`field-button-${fieldId}`}>
      <FieldButtonComponent onClick={() => onFieldClick(fieldId)} {...stateProps} />
    </Box>
  )
}
